import { ContractsGrouping, ContractState } from '@ail/contracts-api'
import { ChartData } from 'chart.js'
import { useMemo } from 'react'
import { Doughnut } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'
import { useGetContractStatisticsQuery } from '../../../redux/api/contractsApi'
import { useFilteredCustomers } from '../../../hooks/useFilteredCustomers'
import { LoadingSpinner } from '../../common/LoadingSpinner/LoadingSpinner'

interface Props {
  tenantId?: string
  companyId?: string
  treeview?: boolean
  color?: 'green' | 'magenta'
}

const options = {
  maintainAspectRatio: false,
  responsive: true,
  borderWidth: 0,
  plugins: {
    datalabels: {
      color: 'white',
    },
    legend: {
      display: true,
    },
  },
}

const initialData: ChartData<'doughnut'> = {
  labels: [],
  datasets: [],
}

const statusesToSkip = [
  ContractState.Canceled,
  ContractState.PayoutPrepared,
  ContractState.PaidOut,
  ContractState.ReadyToBeClosed,
]

export default function ByStatusChart({ color, tenantId, companyId, treeview }: Props) {
  // const activeState = [ContractState.Running, ContractState.Extended]
  // const [selectedState, setSelectedState] = useState<ContractState[] | undefined>(activeState)
  const stats = useGetContractStatisticsQuery({
    grouping: ContractsGrouping.ByState,
    // statuses: selectedState ? selectedState : undefined,
  })

  const { t } = useTranslation('common')
  const { customers } = useFilteredCustomers()
  const relatedCustomers = useMemo(() => {
    if (!tenantId && !companyId) return
    return (
      customers
        ?.filter((c) =>
          companyId
            ? treeview
              ? c.customer?.id === companyId || c.customer?.parentId === companyId
              : c.customer?.id === companyId
            : c.customer?.tenantId === tenantId
        )
        .map((c) => c.customer?.id) ?? []
    )
  }, [companyId, customers, tenantId, treeview])

  const aggregatedData = useMemo(() => {
    if (!stats.data) return
    const calculatedStats: { [key: string]: number } = {}

    Object.entries(stats.data).forEach(([key, value]) => {
      if (!relatedCustomers || relatedCustomers.some((id) => id && key.toLowerCase().includes(id))) {
        Object.entries(value).forEach(([prodKey, prodValue]) => {
          if (statusesToSkip.includes(prodKey as ContractState)) {
            return
          }
          calculatedStats[prodKey] = (calculatedStats[prodKey] || 0) + prodValue
        })
      }
    })
    return calculatedStats
  }, [relatedCustomers, stats.data])

  const chartData = useMemo(() => {
    if (!aggregatedData) {
      return initialData
    }

    const labels: string[] = []
    const values: number[] = []
    const bgColors: string[] = []

    Object.entries(aggregatedData).forEach(([key, value]) => {
      labels.push(key)
      values.push(value)
      bgColors.push(
        key === 'Terminated'
          ? '#FF5555'
          : key === 'Running'
          ? '#39B54A'
          : key === 'Extended'
          ? '#5fcc6d'
          : key === 'Initialized'
          ? '#2BAAE2'
          : key === 'Closed'
          ? '#FF0000'
          : ''
      )
    })
    const data = {
      labels,
      datasets: [
        {
          label: '',
          data: values,
          backgroundColor: bgColors,
        },
      ],
    }
    return data
  }, [aggregatedData])

  // function handleSelect(s: string) {
  //   if (s === 'ALL') {
  //     setSelectedState(undefined)
  //     return
  //   }
  //   setSelectedState(s.split(',') as ContractState[])
  // }
  return (
    <>
      <div className={`box-title ${color}-title`}>
        <span>
          <i className="icon-statistics"></i>
          {t('BY_STATUS')}
        </span>
        {/* <div className="selectArea">
          <select
            defaultValue={activeState}
            className="filterByExpiration"
            onChange={(s) => handleSelect(s.target.value)}
          >
            <option value={activeState}>{t('ACTIVE')}</option>
            <option value={ContractState.Closed}>{t('CLOSED')}</option>
            <option value={ContractState.Initialized}>{t('INITIALIZED')}</option>
            <option value={ContractState.Terminated}>{t('TERMINATED')}</option>
            <option value={'ALL'}>{t('ALL')}</option>
          </select>
          <i className="icon-bottom" />
        </div> */}
      </div>
      {
        <div style={{ height: '400px' }} className="box3 white">
          {!customers ? (
            <div style={{ marginLeft: '50%', marginTop: '50%' }}>
              <LoadingSpinner size={14} />
            </div>
          ) : (
            <Doughnut data={chartData} options={options} />
          )}
        </div>
      }
    </>
  )
}
